import jsPDF from 'jspdf';

import { mascaraCpfCnpj, mascaraPlaca } from '@/nucleo/formato/documentos';
import { COR_PRIMARIA_PDF } from '@/nucleo/pdf/cores';

import type { ComprovanteDeInscricao } from './contrato';

/**
 * Gera e baixa o PDF do comprovante de uma inscrição enviada, com os dados que
 * já estão na tela — não é uma segunda fonte de dado, só outro formato do
 * mesmo `ComprovanteDeInscricao` carregado por `dados.ts`. Um por protocolo:
 * a inscrição de pessoa jurídica gera um protocolo por veículo.
 *
 * A SMT é a dona do sistema (docs/ALVOS.md, "Identificação do dono do
 * sistema corrigida") — o cabeçalho do certificado usa o nome dela, nunca a
 * SME (que segue como persona usuária, não como emissora do documento).
 */
export function baixarComprovanteDeInscricao(inscricao: ComprovanteDeInscricao): void {
  const documento = new jsPDF();
  const largura = documento.internal.pageSize.getWidth();
  let y = 20;

  documento.setFontSize(10);
  documento.setTextColor(100);
  documento.text('PREFEITURA DO MUNICÍPIO DE SÃO PAULO', largura / 2, y, { align: 'center' });
  y += 5;
  documento.text('SECRETARIA MUNICIPAL DE MOBILIDADE URBANA E TRANSPORTE', largura / 2, y, {
    align: 'center',
  });
  y += 5;
  documento.text('TRANSPORTE ESCOLAR GRATUITO — TEG', largura / 2, y, { align: 'center' });
  y += 12;

  documento.setFontSize(16);
  documento.setTextColor(...COR_PRIMARIA_PDF);
  documento.text('CERTIFICADO DE INSCRIÇÃO', largura / 2, y, { align: 'center' });
  y += 10;

  documento.setFontSize(12);
  documento.setTextColor(0);
  documento.text(`Protocolo: ${inscricao.protocolo}`, largura / 2, y, { align: 'center' });
  y += 14;

  documento.setFontSize(11);
  documento.setTextColor(50);

  const campo = (rotulo: string, valor: string) => {
    documento.setFont('helvetica', 'bold');
    documento.text(`${rotulo}:`, 20, y);
    documento.setFont('helvetica', 'normal');
    documento.text(valor, 75, y);
    y += 7;
  };

  campo(inscricao.tipo_pessoa === 'PF' ? 'Nome' : 'Razão social', inscricao.nome_razao_social);
  campo('CPF/CNPJ', mascaraCpfCnpj(inscricao.cpf_cnpj));

  if (inscricao.modalidade) {
    campo('Modalidade', inscricao.modalidade);
  }

  if (inscricao.numero_placa) {
    campo('Placa', mascaraPlaca(inscricao.numero_placa));
  }

  if (inscricao.data_envio) {
    campo('Data de envio', new Date(inscricao.data_envio).toLocaleDateString('pt-BR'));
  }

  y += 20;
  documento.setFontSize(9);
  documento.setTextColor(120);
  documento.text(
    'Este certificado comprova a inscrição no processo de credenciamento do TEG.',
    largura / 2,
    y,
    { align: 'center' },
  );
  y += 5;
  documento.text(
    `Gerado em ${new Date().toLocaleString('pt-BR')}. Documento sem validade jurídica isolado.`,
    largura / 2,
    y,
    { align: 'center' },
  );

  documento.save(`certificado-${inscricao.protocolo}.pdf`);
}
